/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppButton, AppProgressBar } from '../../components';
import { Label, Link, IButton, Dropdown, IDropdownOption } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { TranscriptionRoutePathNames } from '../../routes';
import { LogEvents } from '../../../utils/LogEvents';
import { AppLayoutSettings, getMainPageStyles } from '../../common/styles/common.styles';
import { getRecordingButtonCustomStyles, getBottomLabelStyles, getOneDriveIdentityInfoBarLandingPageStyles as getOneDriveIdentityInfoBarLandingPageSpecificStyles } from './Landing.styles';
import { getFileInputStyles, getDropdownStyles, getSupportedTranscriptionLanguages } from '../../common/pageStyles/LandingPage.styles';
import { LEARN_MORE_URL, ACCEPTED_FILE_FORMATS_FOR_UPLOAD, HOST_PLATFORM_WIN32, DROPDOWN_TRANSCRIPTION_LOCALE } from '../../../AppConstants';
import { AppAuthenticationProviderContext } from '../../context';
import { SetForegroundTaskInStore } from '../../common/actions/LoadingActions';
import { FileUploadForegroundTask } from '../../../backend/fileUpload/FileUploadForegroundTask';
import { HostAppSettings, HostAppSettingName, HostAppName } from '../../../backend/office-js/HostAppSettings';
import { TranscriptionMonthlyQuota } from '../../../backend/transcriptionManager/TranscriptionMonthlyQuota';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { AddTooltipToElement } from '../../components/Tooltip/AddTooltipToElement';
import { LicensePopup } from '../../components/Popups/LicensePopup';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { GetDefaultTranscriptionLocale } from '../../../utils/TranscriptionLanguages';
import { GetAugloopConnectionStatus, AugloopConnectionStatus } from '../../../backend/augloop/Augloop';
import { CreateDefaultRealTimeTranscriptionConnection } from '../../../backend/factory/RealTimeTranscriptionConnectionFactory';
import { ErrorStringFromException, isNullOrUndefined, IsUnlimitedFileUploadMinutesAvailableForUser } from '../../../utils/utils';
import { LandingPageLoaded } from '../../common/actions/LandingActions';
import { TranscriptionIllustration } from '../../common/images/TranscriptionIllustration';
import { getHostAppInfoBar } from '../../components/HostAppInfoBar/HostAppInfoBar';
import { PrimaryIdentityInfo } from '../../../backend/interfaces/IAuthenticationProvider';
import { GetPrimaryIdentity, IsPrimaryIdentityInformationValid } from '../../../backend/office-js/auth/IdentityUtil';
import { getPrimaryAccountInfoStringForLandingPage, shouldShowOnedriveInfobar } from '../Utils';
import { TranscriptionAlert } from '../../components/Popups/TranscriptionAlert';
import { HostAppUtil } from '../../../backend/office-js/HostAppUtil';
import { ShouldShowOneNoteIdentitySwitchDialog, ShowOneNoteIdentitySwitchDialog }  from '../../../backend/OneNote/OneNoteTranscriptionManager';
import { ShouldShowWordIdentitySwitchDialog, ShowWordIdentitySwitchDialog } from '../../../backend/Word/WordTranscriptionManager';
import { BootKeys } from '../../common/telemetry/BootKeys';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { LogError } from '../../../utils/ErrorTelemetry';

export class Landing extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>> {
  declare context: React.ContextType<typeof AppAuthenticationProviderContext>;
  static contextType = AppAuthenticationProviderContext;
  private recordButtonRef = React.createRef<IButton>();
  private fileInputref = React.createRef<HTMLInputElement>();
  private primaryIdentity : PrimaryIdentityInfo | null = null;
  private _isMounted: boolean = false;

  private UploadFile(file: File) {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Upload audio" }]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
    SetForegroundTaskInStore(new FileUploadForegroundTask(file, this.context!, this.props.history));
    if (this.props.location.state.audioBackup != null) {
      this.props.location.state.audioBackup.Close();
      this.props.location.state.audioBackup = null;
    }

    this.props.location.state.realTimeTranscriptionConnection = null;
    this.props.history.replace(TranscriptionRoutePathNames.Loading);
    LogEvents.RenderToPane.EndDurationMeasure(RenderToPaneKeys.DurationSecs);
  }

  public UNSAFE_componentWillMount() {
    this._isMounted = true;
    // We need to update primary identity info if the host (it can be web or win32) has 'ShouldShowOneDriveIdentityInfoDialog' set or the app platform is Win32 as
    // there primary identity info will be needed for identity switch dialog.
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.ShouldShowOneDriveIdentityInfoDialog) || HostAppUtil.GetHostAppPlatform() == HOST_PLATFORM_WIN32) {
      this.updatePrimaryIdentity();
    }
  }

  public componentDidMount() {
    this._isMounted = true;
    this.recordButtonRef.current!.focus();
    LandingPageLoaded();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  public render() {
    if (this.shouldShouldIdentitySwitchDialog()) {
      this.showIdentitySwitchDialog();
    }

    const recordButtonID = "recordButton";
    const uploadButtonID = "uploadButton";
    const isUnlimitedUser = IsUnlimitedFileUploadMinutesAvailableForUser();
    
    // Variables that differ based on user type
    let usedMinutes: number, totalAvailableMinutes: number, timeUsedLabel: string;
    if (!isUnlimitedUser) {
      usedMinutes = TranscriptionMonthlyQuota.GetInstance().GetCachedUsedMinutes();
      totalAvailableMinutes = TranscriptionMonthlyQuota.GetInstance().GetAvailableUploadMinutesPerMonth();
      timeUsedLabel = usedMinutes + "/" + totalAvailableMinutes + TranscriptionStrings.LandingPageTranscriptionTimeUsedLabel;
    }
    
    const percentTimeUsed = TranscriptionMonthlyQuota.GetInstance().GetCachedPercentTimeUsed();
    const styles = getMainPageStyles();

    const _onRecordClicked = (): void => {
      LogEvents.ServiceLiveSpeechToText.Start();
      this.props.history.replace(TranscriptionRoutePathNames.Recording, { realTimeTranscriptionConnection: this.getRealTimeTranscriptionConnection(), audioBackup: this.props.location.state.audioBackup });
    };

    const _onUploadClicked = (): void => {
      let root = document.getElementById('container');
      root?.setAttribute("tabindex", "0");
      root?.focus();
      this.fileInputref.current!.click();
    };

    const _onFileInput = async (event: any) => {
      LogEvents.ServiceLiveSpeechToText.Start();
      this.UploadFile(event.target.files[0]);
    };

    const enableUpload = percentTimeUsed < 1.0; // '1.0' represents 100 percent
    const uploadButton = <AppButton type={'AppButtonProps'} id={uploadButtonID} text={TranscriptionStrings.LandingPageUploadButtonLabel} onExecute={_onUploadClicked} disabled={!enableUpload} />

    const onLanguageDropdownChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, _index?: number | undefined): void => {
      if (option == undefined)
        return;

      if (!isUnlimitedUser) {
        try {
          if(!isNullOrUndefined(window) && !isNullOrUndefined(window.localStorage)) {
            window.localStorage.setItem(DROPDOWN_TRANSCRIPTION_LOCALE, option.key.toString());
          }
        } catch(e) {
          LogError(LogEventNames.Warning, "Error setting dropdown transcription locale in local storage" + ErrorStringFromException(e));
        }
      }

      HostAppSettings.Set(HostAppSettingName.DefaultTranscriptionLocale, option.key.toString());
    };

    // Render bottom section based on user type
    const renderBottomSection = () => {
      if (!isUnlimitedUser) {
        return (
          <div className={styles.bottomLabelContainer}>
            <Label styles={getBottomLabelStyles()}>{timeUsedLabel}</Label>
            <div className={styles.progressContainer}>
              <AppProgressBar type={'AppProgressBarProps'} barHeight={3} percentComplete={percentTimeUsed} />
            </div>
            <Link styles={AppLayoutSettings.AppCommonStyles.getLearnMoreLinkStyles()} href={LEARN_MORE_URL} target="_blank">{TranscriptionStrings.TranscriptionPaneLearnLabel}</Link>
          </div>
        );
      } else {
        return (
          <div className={styles.bottomLabelContainer}>
            <Link styles={AppLayoutSettings.AppCommonStyles.getLearnMoreLinkStyles()} href={LEARN_MORE_URL} target="_blank">{TranscriptionStrings.TranscriptionPaneLearnLabel}</Link>
          </div>
        );
      }
    };

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        {getHostAppInfoBar()}
        <input type="file" ref={this.fileInputref} data-testid="transcription-file-upload-input" style={getFileInputStyles()} onInput={_onFileInput} accept={ACCEPTED_FILE_FORMATS_FOR_UPLOAD}></input>
        <div style={{ flexGrow: 1 }}>
          <div className={styles.labelContainer}>
            <div>
              <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.TranscriptionPaneDescriptionLabelWithUpload}</Label>
            </div>
          </div>
          <div className={ styles.dropDownContainer }>
            <Dropdown
              defaultSelectedKey={ GetDefaultTranscriptionLocale() }
              options={ getSupportedTranscriptionLanguages() }
              styles={ getDropdownStyles }
              onChange={ onLanguageDropdownChange }
              ariaLabel={ TranscriptionStrings.LangSelectAriaLabel }
            />
          </div>
          <div className={styles.buttonContainerLanding}>
            {(enableUpload) ?
              uploadButton
              :
              AddTooltipToElement(uploadButton, TranscriptionStrings.LandingPageUploadButtonLimitCrossedLabel)
            }
            <AppButton buttonRef={this.recordButtonRef} styles={getRecordingButtonCustomStyles()} type={'AppButtonProps'} id={recordButtonID} text={TranscriptionStrings.LandingPageRecordButtonLabel} onExecute={_onRecordClicked} />
          </div>
          <div className={styles.imageContainer}>
            <TranscriptionIllustration />
          </div>
          { shouldShowOnedriveInfobar(this.primaryIdentity) ?
            <div style={ AppLayoutSettings.AppCommonStyles.getOneDriveIdentityInfoBarStyles( getOneDriveIdentityInfoBarLandingPageSpecificStyles()) }>
              { getPrimaryAccountInfoStringForLandingPage(this.primaryIdentity) }
            </div> : null }
        </div>
        {renderBottomSection()}
        <TranscriptionAlert/>
        {
          HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled) ?
            (
              <LicensePopup />
            )
            :
            (
              null
            )
        }
      </div>
    );
  }

  /* This method is used to fetch Active IRealTimeTranscriptionConnnection object, in case
  IRealTimeTranscriptionConnection object exist but in ReadyToConnect state, that connection is closed
  and new Connection object is created and returned.*/
  private getRealTimeTranscriptionConnection() {
    if (GetAugloopConnectionStatus() == AugloopConnectionStatus.ReadyToConnect) {

      // close the existing connection, which is in ready to connect state.
      if (!isNullOrUndefined(this.props.location.state.realTimeTranscriptionConnection)) {
        this.props.location.state.realTimeTranscriptionConnection?.CloseConnection();
      }

      // create new IRealTimeTranscriptionconnection.
      return CreateDefaultRealTimeTranscriptionConnection();
    } else {
      return this.props.location.state.realTimeTranscriptionConnection;
    }
  }

  private async updatePrimaryIdentity() {
    this.primaryIdentity = await GetPrimaryIdentity();
    if (this._isMounted) {
      this.forceUpdate();
    }
  }

  private shouldShouldIdentitySwitchDialog() {
    if (HostAppUtil.GetHostAppPlatform() == HOST_PLATFORM_WIN32 && IsPrimaryIdentityInformationValid(this.primaryIdentity)) {
      if (HostAppUtil.GetHostAppName() == HostAppName.ONENOTE) {
        const shouldShowOneNoteIdentitySwitchDialog = ShouldShowOneNoteIdentitySwitchDialog(this.primaryIdentity?.email);
        LogEvents.Boot.AddData([{ key: BootKeys.IsAccountSwitchDialogShown, value: shouldShowOneNoteIdentitySwitchDialog }]);
        return shouldShowOneNoteIdentitySwitchDialog;
      } else if (HostAppUtil.GetHostAppName() == HostAppName.WORD) {
        const shouldShowWordIdentitySwitchDialog = ShouldShowWordIdentitySwitchDialog(this.primaryIdentity?.email);
        LogEvents.Boot.AddData([{ key: BootKeys.IsAccountSwitchDialogShown, value: shouldShowWordIdentitySwitchDialog }]);
        return shouldShowWordIdentitySwitchDialog;
      }
    }
    // Similarly we can add check for word app where DocumentIdentityEmail will replace NotebookIdentityEmail.
    return false;
  }

  private showIdentitySwitchDialog() {
    const appName = HostAppUtil.GetHostAppName();
    switch (appName) {
      case HostAppName.ONENOTE:
        ShowOneNoteIdentitySwitchDialog(this.primaryIdentity?.email);
        break;
      case HostAppName.WORD:
        ShowWordIdentitySwitchDialog(this.primaryIdentity?.email);
        break;
      default:
    }
  }
}